"use client";

import { getAppointmentTitle } from "@/lib/appointment";
import { fetcher } from "@/lib/fetcher";
import { Appointment } from "@/services/appointments/appointments";
import { useKindeBrowserClient } from "@kinde-oss/kinde-auth-nextjs";
import useSWR from "swr";
import { getAppointmentDisplayDate, getFormattedAppointmentDate } from "@/utils/appointmentDateUtils";
import { Skeleton } from "@/components/ui/skeleton";
interface AppointmentsWidgetProps {
  limit: number;
}

export const AppointmentsWidget = (props: AppointmentsWidgetProps) => {
  const { accessTokenRaw, isLoading: isKindeLoading } = useKindeBrowserClient();

  // Fetch appointments
  const {
    data: appointments,
    error: error,
    isLoading: isLoading,
  } = useSWR(
    accessTokenRaw
      ? [
        `/v1/appointments?limit=${props.limit}&status=open&status=planned`,
        accessTokenRaw,
      ]
      : null,
    ([url, token]) => fetcher(url, token)
  );

  // Handle loading and error states
  if (isKindeLoading || isLoading) return <LoadingState limit={props.limit} />;
  if (error || !accessTokenRaw) return <ErrorState />;

  function isDateWithinDays(appointment: Appointment, days: number) {
    const date = getAppointmentDisplayDate(appointment);
    const now = new Date();
    const diffInDays = (date.getTime() - now.getTime()) / (1000 * 60 * 60 * 24);
    // Only mark true if it's within the *next* 5 days (>= 0 && <= 5)
    return diffInDays >= 0 && diffInDays <= days;
  }

  return (
    <div className="col-span-1 row-span-1 bg-[var(--primary)] rounded-3xl text-white p-4 lg:p-6">
      <p className="text-sm text-gray-300 mb-2">Mijn volgende afspraken</p>
      <h2 className="text-lg lg:text-2xl font-bold mb-4">Volgende afspraken</h2>

      <div className="flex flex-col gap-4">
        {appointments.map((appointment: Appointment, index: number) => {
          // Decide if the date is within 5 days (and the appointment is open)

          const within5Days = isDateWithinDays(appointment, 5);

          return (
            <div
              key={index}
              className="py-2 px-4 rounded-2xl flex items-center bg-white bg-opacity-10 space-x-2 lg:space-x-8"
            >
              <div className="w-24 shrink-0">
                <p className="text-m font-bold">
                  {getFormattedAppointmentDate(appointment)}
                </p>
              </div>

              <span
                className={`block w-1 h-10 rounded-full ${within5Days ? "bg-[var(--warning)]" : "bg-[var(--info)]"
                  } `}
              ></span>

              <div className="flex items-center">
                <p className="text-m font-medium">
                  {getAppointmentTitle(appointment)}
                </p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const LoadingState = ({ limit }: { limit: number }) => {
  return (
    <div className="col-span-1 row-span-1 bg-[var(--primary)] rounded-3xl text-white p-4 lg:p-6">
      <Skeleton className="h-4 w-32 bg-white/10 mb-2" />
      <Skeleton className="h-9 w-48 bg-white/10 mb-4" />

      <Skeleton className="flex flex-col gap-4">
        {[...Array(limit)].map((_, index) => (
          <div
            key={index}
            className="py-2 px-4 rounded-2xl flex items-center bg-white bg-opacity-10 space-x-8"
          >
            <Skeleton className="h-11.5 md:h-10" />
          </div>
        ))}
      </Skeleton>
    </div>
  );
};

const ErrorState = () => {
  return (
    <div className="col-span-1 row-span-1 bg-[var(--primary)] rounded-3xl text-white p-6">
      Er is een fout opgetreden
    </div>
  );
};
